// Calendario de rotación con excepciones. Muestra, por contrato, qué menú del
// ciclo (semanal) cae cada día del mes, y permite desviarse en fechas puntuales:
// sustituir el menú (evento/pedido) o marcar sin servicio (feriado). La excepción
// afecta la producción (op_generar la respeta). Catering → Operación.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ChevronLeft, ChevronRight, Ban, Shuffle, Check, X, Trash2, Loader2, PartyPopper, Factory } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { cat, catRpc, invalidateCatering, SERVICIO_LABEL, DIAS } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Contrato { id: string; nombre: string; ciclo_id: string | null }
interface Receta { id: string; nombre: string }
interface Exc { tipo: 'sustituir' | 'sin_servicio'; motivo: string | null; ambito: 'contrato' | 'global' }
interface Servicio { servicio: string; recetas: string[]; excepcion: Exc | null; pax: number | null }
interface Dia { fecha: string; dia: number; feriado: string | null; tiene_orden: boolean; servicios: Servicio[] }

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const iso = (y: number, m: number, d: number): string => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const srvLbl = (s: string): string => SERVICIO_LABEL[s] ?? s;

type Editor = { fecha: string; servicio: string | null; modo: 'sustituir' | 'sin_servicio'; recetas: string[]; motivo: string };

export function CalendarioCatering() {
  const qc = useQueryClient();
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth()); // 0-11
  const [contratoId, setContratoId] = useState('');
  const [selFecha, setSelFecha] = useState<string | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);

  const desde = iso(anio, mes, 1);
  const hasta = iso(anio, mes, new Date(anio, mes + 1, 0).getDate());

  const { data: contratos = [] } = useQuery<Contrato[]>({
    queryKey: ['cat-contratos-cal'],
    queryFn: () => cat.list<Contrato>('contratos', '?select=id,nombre,ciclo_id&es_activo=is.true&deleted_at=is.null&order=nombre'),
  });
  const conCiclo = contratos.filter((c) => c.ciclo_id);
  const contrato = contratoId || conCiclo[0]?.id || '';

  const { data: dias = [], isLoading } = useQuery<Dia[]>({
    queryKey: ['cat-calendario', contrato, desde, hasta],
    queryFn: () => catRpc<Dia[]>('cat_calendario', { _desde: desde, _hasta: hasta, _contrato_id: contrato }),
    enabled: !!contrato,
  });
  const { data: recetas = [] } = useQuery<Receta[]>({
    queryKey: ['cat-recetas-plato'],
    queryFn: () => catRpc<Receta[]>('recetas_listar', { _tipo: 'plato' }),
  });

  const porFecha = useMemo(() => { const m: Record<string, Dia> = {}; dias.forEach((d) => { m[d.fecha] = d; }); return m; }, [dias]);
  const diaSel = selFecha ? porFecha[selFecha] : null;

  const guardar = useMutation({
    mutationFn: (e: Editor) => catRpc('cat_excepcion_guardar', {
      _fecha: e.fecha, _tipo: e.modo, _contrato_id: contrato, _servicio: e.servicio,
      _recetas: e.modo === 'sustituir' ? e.recetas : null, _motivo: e.motivo || null,
    }),
    onSuccess: () => { invalidateCatering(qc); setEditor(null); toast.success('Excepción guardada'); },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const quitar = useMutation({
    mutationFn: (p: { fecha: string; servicio: string | null }) => catRpc('cat_excepcion_eliminar', { _fecha: p.fecha, _contrato_id: contrato, _servicio: p.servicio }),
    onSuccess: () => { invalidateCatering(qc); toast.success('Excepción quitada'); },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const mover = (delta: number) => { const d = new Date(anio, mes + delta, 1); setAnio(d.getFullYear()); setMes(d.getMonth()); setSelFecha(null); setEditor(null); };

  // rejilla: huecos iniciales según isodow del día 1 (Lun=1)
  const primerDow = new Date(anio, mes, 1).getDay() || 7; // JS: 0=Dom → 7
  const celdas: (Dia | null)[] = [...Array(primerDow - 1).fill(null), ...dias];

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><CalendarDays className="h-4 w-4" /> Calendario de rotación</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div>
              <label className="block text-xs text-muted-foreground">Contrato</label>
              <select className={`${fieldCls} w-72`} value={contrato} onChange={(e) => { setContratoId(e.target.value); setSelFecha(null); setEditor(null); }}>
                {conCiclo.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                {!conCiclo.length && <option value="">— sin contratos con ciclo —</option>}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" onClick={() => mover(-1)}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="min-w-[9rem] text-center text-sm font-medium">{MESES[mes]} {anio}</span>
              <Button size="sm" variant="outline" onClick={() => mover(1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
            <span className="pb-1 text-xs text-muted-foreground">La excepción afecta la producción (op_generar la respeta).</span>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="py-6 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Cargando…</p>}
          {!isLoading && !contrato && <p className="py-6 text-sm text-muted-foreground">Asigna un ciclo a algún contrato para ver su calendario.</p>}
          {!isLoading && contrato && (
            <div className="grid grid-cols-7 gap-1">
              {DIAS.map((d) => <div key={d} className="pb-1 text-center text-[11px] font-medium text-muted-foreground">{d.slice(0, 3)}</div>)}
              {celdas.map((d, i) => d === null ? <div key={`b${i}`} /> : (
                <button key={d.fecha} onClick={() => { setSelFecha(d.fecha); setEditor(null); }}
                  className={`min-h-[74px] rounded-md border p-1 text-left align-top transition hover:border-primary ${selFecha === d.fecha ? 'border-primary ring-1 ring-primary' : ''} ${d.feriado ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold">{d.dia === 7 ? '' : ''}{new Date(d.fecha + 'T12:00').getDate()}</span>
                    <span className="flex items-center gap-0.5">
                      {d.tiene_orden && <Factory className="h-3 w-3 text-emerald-600" aria-label="orden generada" />}
                      {d.feriado && <PartyPopper className="h-3 w-3 text-amber-600" aria-label={d.feriado} />}
                    </span>
                  </div>
                  <div className="mt-0.5 space-y-0.5">
                    {d.servicios.slice(0, 3).map((s) => (
                      <div key={s.servicio} className="flex items-center gap-0.5 truncate text-[10px] leading-tight">
                        {s.excepcion?.tipo === 'sin_servicio' && <Ban className="h-2.5 w-2.5 shrink-0 text-red-600" />}
                        {s.excepcion?.tipo === 'sustituir' && <Shuffle className="h-2.5 w-2.5 shrink-0 text-sky-600" />}
                        <span className={`truncate ${s.excepcion?.tipo === 'sin_servicio' ? 'text-red-600 line-through' : s.excepcion ? 'text-sky-700 dark:text-sky-400' : 'text-muted-foreground'}`}>
                          {srvLbl(s.servicio).replace('Almuerzo Op. ', 'Alm.')}: {s.excepcion?.tipo === 'sin_servicio' ? '—' : (s.recetas[0]?.replace('Plato fuerte — ', '') ?? '—')}
                        </span>
                      </div>
                    ))}
                    {!d.servicios.length && <span className="text-[10px] text-muted-foreground">sin menú</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detalle / editor del día */}
      {diaSel && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex flex-wrap items-center gap-2 text-sm">
              <CalendarDays className="h-4 w-4" /> {new Date(diaSel.fecha + 'T12:00').toLocaleDateString(LOCALE, { weekday: 'long', day: 'numeric', month: 'long' })}
              {diaSel.feriado && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Feriado: {diaSel.feriado}</span>}
              {diaSel.tiene_orden && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">Orden generada</span>}
            </CardTitle>
            <div className="pt-1">
              <Button size="sm" variant="outline" className="gap-1 text-red-600" onClick={() => setEditor({ fecha: diaSel.fecha, servicio: null, modo: 'sin_servicio', recetas: [], motivo: diaSel.feriado ?? '' })}>
                <Ban className="h-4 w-4" /> Todo el día sin servicio
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {diaSel.servicios.map((s) => (
              <div key={s.servicio} className="rounded-md border p-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{srvLbl(s.servicio)}</span>
                  {s.pax != null && <span className="text-xs text-muted-foreground">{num(s.pax, 0)} pax</span>}
                  {s.excepcion?.tipo === 'sin_servicio' && <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] text-red-700 dark:bg-red-900/30 dark:text-red-300"><Ban className="h-3 w-3" /> Sin servicio</span>}
                  {s.excepcion?.tipo === 'sustituir' && <span className="flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] text-sky-700 dark:bg-sky-900/30 dark:text-sky-300"><Shuffle className="h-3 w-3" /> Sustituido</span>}
                  <div className="ml-auto flex gap-1">
                    <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={() => setEditor({ fecha: diaSel.fecha, servicio: s.servicio, modo: 'sustituir', recetas: [], motivo: s.excepcion?.motivo ?? '' })}><Shuffle className="h-3 w-3" /> Sustituir</Button>
                    <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={() => setEditor({ fecha: diaSel.fecha, servicio: s.servicio, modo: 'sin_servicio', recetas: [], motivo: s.excepcion?.motivo ?? '' })}><Ban className="h-3 w-3" /> Sin servicio</Button>
                    {s.excepcion && <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs text-muted-foreground" disabled={quitar.isPending} onClick={() => quitar.mutate({ fecha: diaSel.fecha, servicio: s.servicio })}><Trash2 className="h-3 w-3" /> Quitar</Button>}
                  </div>
                </div>
                <p className="pt-1 text-xs text-muted-foreground">
                  {s.excepcion?.tipo === 'sin_servicio' ? <span className="text-red-600">No se produce este servicio{s.excepcion.motivo ? ` — ${s.excepcion.motivo}` : ''}.</span>
                    : s.recetas.length ? s.recetas.map((x) => x.replace('Plato fuerte — ', '')).join(' · ')
                    : 'Sin recetas en el ciclo para este día.'}
                  {s.excepcion?.ambito === 'global' && <span className="ml-1 rounded bg-muted px-1 text-[10px]">excepción global</span>}
                </p>
              </div>
            ))}
            {!diaSel.servicios.length && <p className="text-sm text-muted-foreground">Este día no tiene menú en el ciclo. Puedes marcar el día completo o agregar el menú en Menús cíclicos.</p>}
          </CardContent>
        </Card>
      )}

      {/* Formulario de excepción */}
      {editor && diaSel && (
        <Card className="border-primary">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {editor.modo === 'sin_servicio' ? 'Marcar sin servicio' : 'Sustituir menú'} — {editor.servicio ? srvLbl(editor.servicio) : 'todo el día'} · {new Date(editor.fecha + 'T12:00').toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' })}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {editor.modo === 'sustituir' && (
              <div>
                <label className="block text-xs text-muted-foreground">Recetas del menú sustituto</label>
                <div className="mt-1 max-h-52 overflow-y-auto rounded-md border p-1">
                  {recetas.map((r) => {
                    const on = editor.recetas.includes(r.id);
                    return (
                      <label key={r.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-accent">
                        <input type="checkbox" checked={on} onChange={() => setEditor({ ...editor, recetas: on ? editor.recetas.filter((x) => x !== r.id) : [...editor.recetas, r.id] })} />
                        {r.nombre.replace('Plato fuerte — ', '')}
                      </label>
                    );
                  })}
                  {!recetas.length && <p className="p-1 text-xs text-muted-foreground">No hay recetas de plato.</p>}
                </div>
                <p className="pt-0.5 text-[11px] text-muted-foreground">{editor.recetas.length} seleccionada(s) — reemplazan el menú del ciclo para este día/servicio.</p>
              </div>
            )}
            <div>
              <label className="block text-xs text-muted-foreground">Motivo</label>
              <input className={`${fieldCls} w-full`} value={editor.motivo} onChange={(e) => setEditor({ ...editor, motivo: e.target.value })} placeholder={editor.modo === 'sin_servicio' ? 'Feriado, cierre…' : 'Evento, pedido del cliente…'} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="gap-1" disabled={guardar.isPending || (editor.modo === 'sustituir' && !editor.recetas.length)} onClick={() => guardar.mutate(editor)}>
                {guardar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Guardar
              </Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setEditor(null)}><X className="h-4 w-4" /> Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
